import { DeviceMobile, DesktopTower, DownloadSimple, BellRinging, CheckCircle, BookOpen, Megaphone } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import InstallButton from "@/components/InstallButton";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";

export default function CompanionInfo() {
  const { enablePush, pushOn } = useNotifications();
  const { hasRole } = useAuth();

  const downloadManual = () => {
    const token = localStorage.getItem("hgs_token");
    window.location.href = `${API}/manual/download?auth=${token}`;
  };
  const announceBling = async () => {
    try {
      const { data } = await api.post("/companion/announce", {});
      toast.success(`Anunț trimis (v${data.version}) către ${data.notified} utilizatori`);
    } catch { toast.error("Nu am putut trimite anunțul"); }
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-8 fade-up" data-testid="companion-page">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Companion</p>
      <h1 className="font-head text-3xl font-extrabold tracking-tighter">Notificări „Bling"</h1>
      <p className="mt-2 max-w-xl text-sm text-muted-foreground">
        Primește notificările Bling <strong>native, chiar și cu aplicația închisă</strong> — pe telefon (Android/iOS)
        și pe PC. Cel mai simplu: instalează aplicația și apasă „Activează".
      </p>

      {/* Recomandat: PWA (mobil + PC) */}
      <div className="mt-6 rounded-xl border border-primary/30 bg-primary/[0.05] p-6" data-testid="bling-pwa-card">
        <div className="flex items-center gap-2">
          <DeviceMobile size={22} weight="fill" className="text-primary" />
          <h3 className="font-head text-lg font-bold">Recomandat — Aplicația web (mobil & PC)</h3>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Fără instalări complicate. Adaugi Highberg pe ecranul principal / desktop și activezi notificările dintr-un click.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <InstallButton />
          <Button onClick={enablePush} disabled={pushOn} data-testid="companion-enable-push" className="gap-2" variant={pushOn ? "default" : "secondary"}>
            {pushOn ? <CheckCircle size={18} weight="fill" /> : <BellRinging size={18} weight="fill" />}
            {pushOn ? "Notificări activate pe acest dispozitiv" : "Activează notificările pe acest dispozitiv"}
          </Button>
        </div>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="mb-1 flex items-center gap-1.5 font-semibold"><DeviceMobile size={16} className="text-emerald-400" /> Pe telefon</div>
            <ol className="list-decimal space-y-1 pl-4 text-xs text-muted-foreground">
              <li><strong className="text-foreground">iPhone/iPad:</strong> deschide obligatoriu în <strong className="text-foreground">Safari</strong> (nu în Chrome!), apasă Partajează → „Adaugă pe ecranul principal".</li>
              <li><strong className="text-foreground">Android:</strong> apasă butonul <strong className="text-foreground">„Instalează aplicația"</strong> de mai sus (sau meniul ⋮ → „Instalează aplicația").</li>
              <li>Deschide Highberg de pe ecranul principal și apasă <strong>Activează</strong>.</li>
            </ol>
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="mb-1 flex items-center gap-1.5 font-semibold"><DesktopTower size={16} className="text-blue-400" /> Pe PC</div>
            <ol className="list-decimal space-y-1 pl-4 text-xs text-muted-foreground">
              <li>În Chrome/Edge, apasă butonul <strong>„Instalează aplicația"</strong> de mai sus (sau iconița <strong>Instalează</strong> din bara de adrese, ori ⋮ → „Instalează Highberg").</li>
              <li>Aplicația se deschide într-o fereastră proprie.</li>
              <li>Apasă <strong>Activează</strong> ca să primești notificări native.</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Manual & ghiduri (fără descărcare de aplicație) */}
      <div className="mt-6 rounded-xl border border-border bg-card p-6" data-testid="bling-manual-card">
        <div className="flex items-center gap-2">
          <BookOpen size={22} weight="bold" className="text-violet-400" />
          <h3 className="font-head text-lg font-bold">Manual de utilizare</h3>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Ghid pas cu pas al fluxului de lucru, pe departamente — util pentru sesiunile cu echipa.
        </p>
        <div className="mt-4 flex flex-wrap items-start gap-4">
          <div>
            <Button onClick={downloadManual} data-testid="download-manual-btn" variant="outline" className="gap-2">
              <DownloadSimple size={18} weight="bold" /> Descarcă Manualul (DOCX)
            </Button>
            <p className="mt-2 max-w-xs text-xs text-muted-foreground">
              Ghid pas cu pas al fluxului de lucru, pe departamente — pentru sesiunile cu echipa.
            </p>
          </div>
          <div>
            <Link to="/manual">
              <Button data-testid="open-manual-btn" variant="outline" className="gap-2">
                <BookOpen size={18} weight="bold" /> Vezi manualul în aplicație
              </Button>
            </Link>
            <p className="mt-2 max-w-xs text-xs text-muted-foreground">
              Citește manualul direct în platformă, pe capitole, fără să descarci nimic.
            </p>
          </div>
        </div>
      </div>

      {hasRole("admin") && (
        <div className="mt-6 rounded-xl border border-amber-500/30 bg-amber-500/[0.05] p-6" data-testid="bling-announce-card">
          <div className="flex items-center gap-2">
            <Megaphone size={22} weight="bold" className="text-amber-400" />
            <h3 className="font-head text-lg font-bold">Anunță o versiune nouă de Bling (admin)</h3>
          </div>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
            Când publici o versiune nouă a aplicației Bling, apasă butonul de mai jos: toți utilizatorii primesc o
            notificare cu buton de instalare directă și văd un banner „Actualizează Bling" pe Dashboard.
          </p>
          <Button onClick={announceBling} data-testid="bling-announce-btn" variant="secondary" className="mt-4 gap-2">
            <Megaphone size={18} weight="bold" /> Anunță actualizarea acum
          </Button>
        </div>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-5">
          <BellRinging size={22} className="text-primary" weight="bold" />
          <div className="mt-2 font-semibold">Notificări native</div>
          <div className="text-sm text-muted-foreground">Alerte pe ecran chiar și cu aplicația închisă, pe telefon și PC.</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <DeviceMobile size={22} className="text-emerald-400" weight="bold" />
          <div className="mt-2 font-semibold">Un singur cont</div>
          <div className="text-sm text-muted-foreground">Aceleași notificări pe toate dispozitivele tale.</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <CheckCircle size={22} className="text-violet-400" weight="bold" />
          <div className="mt-2 font-semibold">Zero configurare</div>
          <div className="text-sm text-muted-foreground">Instalezi, apeși „Activează" și gata. Poți dezactiva oricând din clopoțel.</div>
        </div>
      </div>
    </div>
  );
}
